import React, { useState, useEffect, useRef } from 'react';
import { Toaster } from 'react-hot-toast';
import ImmersiveLayout from './ImmersivedLayout';
import PaymentForm from './PaymentForm';
import MapView from './MapView';
import { chatWithGPT } from '../services/openaiService';
import axios from 'axios';

/**
 * ImmersiveShoppingExperience
 *
 * Wrapper that combines:
 * - New immersive UI (fullscreen views, animations)
 * - Existing ChatInterface logic (GPT, MCP UI, products/facilities)
 */
const ImmersiveShoppingExperience = () => {
  const [viewMode, setViewMode] = useState('products'); // Start with products view
  const [chatExpanded, setChatExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [products, setProducts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productFilters, setProductFilters] = useState({}); // For MCP UI filtering
  const [selectedProduct, setSelectedProduct] = useState(null); // For product details view
  const [userLocations, setUserLocations] = useState({
    home: null,
    work: null,
    current: null
  });
  const [facilitiesTotal, setFacilitiesTotal] = useState(0); // Total count from API
  const [facilitiesPage, setFacilitiesPage] = useState(0); // Current page for lazy loading
  const [facilitiesFilters, setFacilitiesFilters] = useState({}); // Store current filters
  const [loadingMore, setLoadingMore] = useState(false); // Loading more facilities
  const [checkoutProduct, setCheckoutProduct] = useState(null); // Product pending checkout
  const [checkoutSessionId, setCheckoutSessionId] = useState(null); // Stripe checkout session ID
  const [showPaymentForm, setShowPaymentForm] = useState(false); // Show payment modal
  const [showMapView, setShowMapView] = useState(false); // Show map modal

  // Track processed intents to prevent duplicates
  const lastProcessedIntent = useRef(null);

  // Fetch initial products
  useEffect(() => {
    fetchProducts();
  }, []);

  // Listen for intent messages from product cards (iframe postMessage) and same-window messages
  useEffect(() => {
    const handleIntent = (event) => {
      // Handle messages from both iframes AND same window (for product details view)
      // Note: iframe messages have event.source !== window, but we also need same-window messages

      if (event.data && event.data.type === 'intent') {
        const { intent, params } = event.data.payload;

        // Create unique key for this intent
        const intentKey = `${intent}-${params.productId}-${Date.now()}`;

        // Debounce: ignore if same intent processed in last 1 second
        if (lastProcessedIntent.current) {
          const [lastIntent, lastProductId, lastTime] = lastProcessedIntent.current.split('-');
          if (lastIntent === intent && lastProductId === params.productId &&
              Date.now() - parseInt(lastTime) < 1000) {
            console.log('Ignoring duplicate intent:', intent, params);
            return;
          }
        }

        lastProcessedIntent.current = intentKey;
        console.log('Processing intent:', intent, params);

        if (intent === 'explore') {
          // Show product details - pass product ID to GPT
          handleUserMessage(`[USER_INTENT: explore | product_id: ${params.productId}] Pokaż mi szczegóły tego produktu`);
        } else if (intent === 'purchase') {
          // Trigger purchase flow - pass product ID to GPT
          handleUserMessage(`[USER_INTENT: purchase | product_id: ${params.productId}] Chcę kupić ten produkt`);
        }
      }
    };

    window.addEventListener('message', handleIntent);
    return () => window.removeEventListener('message', handleIntent);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      console.log('Fetching products from:', process.env.REACT_APP_N8N_PRODUCTS_URL);
      const response = await axios.get(`${process.env.REACT_APP_N8N_PRODUCTS_URL}`);
      console.log('Products response:', response.data);
      const productsData = response.data.products || [];
      console.log('Setting products:', productsData.length, 'items');
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Fetch facilities from middleware with pagination support
  const fetchFacilities = async (filters = {}, append = false) => {
    try {
      const PAGE_SIZE = 20;
      const page = append ? facilitiesPage + 1 : 0;

      if (append) {
        setLoadingMore(true);
      }

      const queryParams = new URLSearchParams();
      if (filters.city) queryParams.append('city', filters.city);
      if (filters.facility_type) queryParams.append('facility_type', filters.facility_type);
      queryParams.append('limit', PAGE_SIZE.toString());
      queryParams.append('offset', (page * PAGE_SIZE).toString());

      // Use /api/facilities (JSON data) instead of /api/facilities-ui (HTML)
      const url = `${process.env.REACT_APP_MCP_MIDDLEWARE_URL}/api/facilities${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      console.log('Fetching facilities from:', url);
      const response = await axios.get(url);
      console.log('Facilities response:', response.data);

      // Extract facilities from JSON response
      const facilitiesData = response.data.facilities || [];
      const total = response.data.total || facilitiesData.length;

      // Transform data to match expected format in FullscreenView
      const transformedFacilities = facilitiesData.map(f => ({
        ...f,
        address: f.address?.street ? `${f.address.street}, ${f.address.city}` : f.address?.city || 'Brak adresu',
        amenities: f.amenities || []
      }));

      if (append) {
        setFacilities(prev => [...prev, ...transformedFacilities]);
      } else {
        setFacilities(transformedFacilities);
        setFacilitiesFilters(filters); // Store filters for "load more"
      }

      setFacilitiesTotal(total);
      setFacilitiesPage(page);
      setViewMode('facilities');
    } catch (error) {
      console.error('Error fetching facilities:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Load more facilities
  const loadMoreFacilities = () => {
    fetchFacilities(facilitiesFilters, true);
  };

  // Handle user messages
  const handleUserMessage = async (userMessage) => {
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setChatExpanded(true);

    try {
      // Get available filters
      const availableFilters = {
        cardTypes: [...new Set(products.map(p => p.card_type))],
        durations: [...new Set(products.map(p => p.duration_months))],
        facilities: [...new Set(products.flatMap(p => p.facilities || []))],
        cities: [...new Set(products.flatMap(p => p.cities || []))],
        priceRange: {
          min: Math.min(...products.map(p => p.price)),
          max: Math.max(...products.map(p => p.price))
        }
      };

      const textMessages = messages.filter(msg => typeof msg.content === 'string');

      const gptResponse = await chatWithGPT(
        [...textMessages, { role: 'user', content: userMessage }],
        products,
        availableFilters,
        userLocations
      );

      console.log('GPT Response:', gptResponse);

      // Handle function calls
      if (gptResponse.function_call) {
        const functionName = gptResponse.function_call.name;
        const functionArgs = JSON.parse(gptResponse.function_call.arguments);

        if (functionName === 'show_facilities_ui') {
          await fetchFacilities(functionArgs);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `Znalazłem obiekty sportowe ${functionArgs.city ? `w ${functionArgs.city}` : ''}!`
          }]);
        } else if (functionName === 'show_products_ui') {
          // Build filter query for middleware MCP UI
          const filters = {};

          if (functionArgs.card_type) {
            filters.cardType = functionArgs.card_type;
          }
          // GPT sends min_price/max_price (snake_case)
          if (functionArgs.min_price) {
            filters.minPrice = functionArgs.min_price;
          }
          if (functionArgs.max_price) {
            filters.maxPrice = functionArgs.max_price;
          }
          if (functionArgs.duration) {
            filters.duration = functionArgs.duration;
          }
          if (functionArgs.facility) {
            filters.facility = functionArgs.facility;
          }
          if (functionArgs.city) {
            filters.city = functionArgs.city;
          }
          // GPT sends requires_* (snake_case)
          if (functionArgs.requires_personal_training) {
            filters.requiresPersonalTraining = 'true';
          }
          if (functionArgs.requires_swimming_pool) {
            filters.requiresSwimmingPool = 'true';
          }
          if (functionArgs.requires_spa) {
            filters.requiresSpa = 'true';
          }

          // Set filters to trigger FullscreenView refresh
          setProductFilters(filters);
          setViewMode('products');

          setMessages(prev => [...prev, {
            role: 'assistant',
            content: gptResponse.content || `Filtruję produkty według Twoich kryteriów...`
          }]);
        } else if (functionName === 'show_product_details') {
          // Fetch and display detailed product information
          const productId = functionArgs.product_id;
          console.log('Looking for product ID:', productId);

          // First try local products state
          let product = products.find(p => p.id === productId);

          // If not found locally, fetch from API
          if (!product) {
            console.log('Product not in local state, fetching from API...');
            try {
              const response = await axios.get(`${process.env.REACT_APP_N8N_PRODUCTS_URL}`);
              const allProducts = response.data.products || [];
              product = allProducts.find(p => p.id === productId);
              // Update local state for future lookups
              if (allProducts.length > 0) {
                setProducts(allProducts);
              }
            } catch (err) {
              console.error('Error fetching products:', err);
            }
          }

          console.log('Found product:', product);

          if (product) {
            // Show product details in main view
            setSelectedProduct(product);
            setViewMode('productDetails');

            setMessages(prev => [...prev, {
              role: 'assistant',
              content: `Oto szczegóły karnetu "${product.title}". Jeśli chcesz go kupić, kliknij przycisk "Kup teraz" lub powiedz "kup".`
            }]);
          } else {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: 'Przepraszam, nie mogę znaleźć szczegółów tego produktu.'
            }]);
          }
        } else if (functionName === 'recommend_products_for_route') {
          // Location-based recommendation - find gyms along commute route
          if (!userLocations.home || !userLocations.work) {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: 'Aby polecić karnety na podstawie trasy, potrzebuję Twoich lokalizacji. Kliknij "Moja lokalizacja" w górnym menu i ustaw adres domu i pracy.'
            }]);
          } else {
            try {
              // Call gym-route API
              const routeResponse = await axios.post(
                `${process.env.REACT_APP_MCP_MIDDLEWARE_URL}/api/gym-route`,
                {
                  startAddress: userLocations.home.address,
                  endAddress: userLocations.work.address,
                  maxDetour: functionArgs.max_detour || 1000,
                  maxResults: 20
                }
              );

              const facilitiesOnRoute = routeResponse.data.facilities || [];
              const routeInfo = routeResponse.data.route || {};

              if (facilitiesOnRoute.length > 0) {
                // Analyze facilities to recommend best products
                const facilityTypes = [...new Set(facilitiesOnRoute.map(f => f.type || f.facility_type))];

                // Find products that match these facility types
                const matchingProducts = products.filter(p => {
                  // Check if product facilities overlap with route facilities
                  const productFacilities = p.facilities || [];
                  return facilityTypes.some(ft => {
                    if (ft === 'Fitness Center') return productFacilities.some(pf => ['Gym', 'CrossFit', 'Martial Arts'].includes(pf));
                    if (ft === 'Swimming Pool') return p.includes_swimming_pool;
                    if (ft === 'Yoga Studio') return productFacilities.includes('Yoga');
                    return productFacilities.some(pf => pf.toLowerCase().includes(ft.toLowerCase()));
                  });
                });

                // Sort by value (features per dollar)
                const sortedProducts = matchingProducts.sort((a, b) => {
                  const scoreA = (a.facilities?.length || 0) / a.price;
                  const scoreB = (b.facilities?.length || 0) / b.price;
                  return scoreB - scoreA;
                });

                // Show top 3 recommendations
                const topProducts = sortedProducts.slice(0, 3);

                setMessages(prev => [...prev, {
                  role: 'assistant',
                  content: `🗺️ Znalazłem ${facilitiesOnRoute.length} obiektów sportowych na Twojej trasie (${routeInfo.distance_km} km, ${routeInfo.duration_minutes} min)!

📍 Typy obiektów po drodze: ${facilityTypes.join(', ')}

🏆 **Moje rekomendacje dla Twojej trasy:**
${topProducts.map((p, i) => `${i + 1}. **${p.title}** ($${p.price}) - ${p.facilities?.slice(0, 4).join(', ')}`).join('\n')}

💡 Kliknij "Mapa obiektów" aby zobaczyć wszystkie siłownie na mapie, lub powiedz np. "pokaż karnet ${topProducts[0]?.title}" żeby poznać szczegóły.`
                }]);

                // Show products view with relevant filters
                setProductFilters({});
                setViewMode('products');
              } else {
                setMessages(prev => [...prev, {
                  role: 'assistant',
                  content: `Nie znalazłem obiektów sportowych w promieniu 1km od Twojej trasy. Spróbuj zwiększyć promień wyszukiwania lub sprawdź "Mapa obiektów" aby zobaczyć wszystkie siłownie.`
                }]);
              }
            } catch (error) {
              console.error('Route recommendation error:', error);
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Przepraszam, wystąpił błąd podczas wyszukiwania obiektów po drodze. Spróbuj ponownie lub użyj "Mapa obiektów".'
              }]);
            }
          }
        } else if (functionName === 'initiate_checkout') {
          // Start Stripe checkout process
          const productId = functionArgs.product_id;
          console.log('Checkout - looking for product ID:', productId);

          // First try local products state
          let product = products.find(p => p.id === productId);

          // If not found locally, fetch from API
          if (!product) {
            console.log('Product not in local state, fetching from API...');
            try {
              const response = await axios.get(`${process.env.REACT_APP_N8N_PRODUCTS_URL}`);
              const allProducts = response.data.products || [];
              product = allProducts.find(p => p.id === productId);
              // Update local state for future lookups
              if (allProducts.length > 0) {
                setProducts(allProducts);
              }
            } catch (err) {
              console.error('Error fetching products for checkout:', err);
            }
          }

          console.log('Found product for checkout:', product);

          if (product) {
            // Store product for checkout button
            setCheckoutProduct(product);

            setMessages(prev => [...prev, {
              role: 'assistant',
              content: `Przygotowuję proces płatności dla: ${product.title} ($${product.price})... 🛒`
            }]);
          } else {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: 'Przepraszam, nie mogę znaleźć tego produktu. Spróbuj odświeżyć stronę.'
            }]);
          }
        }
      } else if (gptResponse.content) {
        // Regular text response
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: gptResponse.content
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Przepraszam, wystąpił błąd. Spróbuj ponownie.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle location updates
  const handleLocationUpdate = (locationType, location) => {
    console.log('Location update:', locationType, location);

    setUserLocations(prev => ({
      ...prev,
      [locationType]: location
    }));

    // Auto-send message to AI
    const cityOrAddress = location.city || location.address?.split(',')[0] || 'tej lokalizacji';

    if (locationType === 'home') {
      handleUserMessage(`Mieszkam w ${cityOrAddress}. Pokaż mi siłownie w pobliżu.`);
    } else if (locationType === 'work') {
      if (userLocations.home) {
        handleUserMessage(`Pracuję w ${cityOrAddress}. Znajdź obiekty po drodze z domu do pracy.`);
      } else {
        handleUserMessage(`Pracuję w ${cityOrAddress}. Pokaż mi siłownie w pobliżu.`);
      }
    }
  };

  // Handle Stripe checkout - show PaymentForm modal
  const handleCheckout = async (product) => {
    try {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Otwieram formularz płatności...`
      }]);

      // Call N8N webhook to create Stripe checkout session
      // N8N expects items array format
      const response = await axios.post(process.env.REACT_APP_N8N_CHECKOUT_URL, {
        items: [{
          product_id: product.id,
          name: product.title,
          price: product.price,
          quantity: 1
        }],
        // Also send flat format for backwards compatibility
        product_id: product.id,
        product_name: product.title,
        price: product.price
      });

      console.log('Checkout session response:', response.data);

      // Get checkout_session_id from N8N response
      const sessionId = response.data?.checkout_session_id || response.data?.id || `demo_session_${Date.now()}`;

      setCheckoutSessionId(sessionId);
      setCheckoutProduct(product);
      setShowPaymentForm(true);

    } catch (error) {
      console.error('Checkout error:', error);
      // Demo mode - show payment form anyway with demo session
      setCheckoutSessionId(`demo_session_${Date.now()}`);
      setCheckoutProduct(product);
      setShowPaymentForm(true);
    }
  };

  // Handle payment success
  const handlePaymentSuccess = (result) => {
    console.log('Payment success:', result);
    setShowPaymentForm(false);
    setCheckoutProduct(null);
    setCheckoutSessionId(null);

    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `🎉 Płatność zakończona sukcesem!\n\nDziękujemy za zakup karnetu "${checkoutProduct?.title}".\nOtrzymasz email z potwierdzeniem.`
    }]);
  };

  // Handle payment cancel
  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setCheckoutProduct(null);
    setCheckoutSessionId(null);

    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `Płatność anulowana. Jeśli chcesz kontynuować zakup, kliknij ponownie przycisk "Kup".`
    }]);
  };

  // Handle view changes from UI
  const handleViewChange = async (newView, data = {}) => {
    // Handle special 'map' view
    if (newView === 'map') {
      // Load facilities if not already loaded
      if (facilities.length === 0) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_MCP_MIDDLEWARE_URL}/api/facilities?limit=100`
          );
          const facilitiesData = response.data.facilities || [];
          setFacilities(facilitiesData);
          setFacilitiesTotal(response.data.total || facilitiesData.length);
        } catch (error) {
          console.error('Error loading facilities for map:', error);
        }
      }
      setShowMapView(true);
      return;
    }

    setViewMode(newView);

    if (data.facilities) {
      setFacilities(data.facilities);
    }

    if (data.triggerProductSearch && data.facilityContext) {
      // User clicked "Buy Pass" - delegate to AI
      // Note: city can be in facilityContext.city or facilityContext.address.city
      const facilityCity = data.facilityContext.city || data.facilityContext.address?.city || 'nieznane miasto';
      const message = `[USER_INTENT: find_products_for_facility | facility_name: ${data.facilityContext.name} | facility_type: ${data.facilityContext.facility_type} | city: ${facilityCity}] Użytkownik kliknął "Kup karnet" przy obiekcie "${data.facilityContext.name}" (Typ: ${data.facilityContext.facility_type}) w ${facilityCity}. Znajdź karnety FitPass które dają dostęp do tego typu obiektów.`;

      handleUserMessage(message);
    }
  };

  // Handle facility selection from map - find products for it
  const handleMapFacilitySelect = (facility) => {
    setShowMapView(false);
    const facilityCity = facility.city || 'nieznane miasto';
    const message = `[USER_INTENT: find_products_for_facility | facility_name: ${facility.name} | facility_type: ${facility.type} | city: ${facilityCity}] Użytkownik wybrał obiekt "${facility.name}" (Typ: ${facility.type}) z mapy. Znajdź karnety FitPass które dają dostęp do tego typu obiektów.`;
    handleUserMessage(message);
  };

  // Handle route finding results from map
  const handleRouteFound = (routeData) => {
    const facilityCount = routeData.facilities?.length || 0;
    if (facilityCount > 0) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Znalazłem ${facilityCount} obiektów sportowych po drodze z domu do pracy (${routeData.route?.distance_km} km, ${routeData.route?.duration_minutes} min). Kliknij na marker na mapie, aby zobaczyć karnety dla danego obiektu.`
      }]);
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <ImmersiveLayout
        viewMode={viewMode}
        facilities={facilities}
        facilitiesTotal={facilitiesTotal}
        loadingMore={loadingMore}
        onLoadMoreFacilities={loadMoreFacilities}
        products={products}
        productFilters={productFilters}
        selectedProduct={selectedProduct}
        messages={messages}
        chatExpanded={chatExpanded}
        userLocations={userLocations}
        isLoading={isLoading}
        onChatToggle={() => setChatExpanded(!chatExpanded)}
        onSendMessage={handleUserMessage}
        onViewChange={handleViewChange}
        onLocationUpdate={handleLocationUpdate}
        onCheckout={handleCheckout}
        checkoutProduct={checkoutProduct}
      />

      {/* Stripe Payment Form Modal */}
      {showPaymentForm && checkoutProduct && (
        <PaymentForm
          product={checkoutProduct}
          checkoutSessionId={checkoutSessionId}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      )}

      {/* Map View Modal */}
      {showMapView && (
        <MapView
          facilities={facilities}
          userLocations={userLocations}
          onClose={() => setShowMapView(false)}
          onFacilitySelect={handleMapFacilitySelect}
          onFindRoute={handleRouteFound}
        />
      )}
    </>
  );
};

export default ImmersiveShoppingExperience;
